// src/pages/DeviceDiagnostics.jsx
// Supporto · Diagnostica dispositivo: il cliente collega la scheda GPS
// (NXR01B, NXR01C) al computer con il cavo USB e la pagina, leggendo i log che
// la scheda stampa all'avvio, dice se funziona e quale componente è guasto.
// Nessuna modifica al firmware e nessun dato inviato al server.
// Regole in diagnostics/nxrRules.js, motore in diagnostics/nxrEngine.js,
// seriale in diagnostics/serialLink.js.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../components/icons';
import { NXR_RULES, CHECKS } from '../diagnostics/nxrRules';
import { evaluate, finalize, LineSplitter, STATUS_LABEL } from '../diagnostics/nxrEngine';
import { SerialLink, serialSupport, serialError, describePort } from '../diagnostics/serialLink';
import './DeviceDiagnostics.css';

const MAX_LINES = 50000;
const TICK_MS = 250;
// righe senza esito propria durante il test (calcolate o chieste a fine avvio)
const NO_SPINNER = new Set(['charge', 'signal', 'led']);

const fmtClock = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const fmtStamp = (d) => new Intl.DateTimeFormat('it-IT', { dateStyle: 'short', timeStyle: 'medium' }).format(d);

function StatusIcon({ status, spinning }) {
  if (spinning) return <span className="dg-st dg-st--run"><span className="rs-spinner" /></span>;
  const ic = { ok: <Icon.Check size={15} />, fail: <Icon.X size={15} />, warn: <Icon.Alert size={14} />, na: <span className="dg-st__dash" /> }[status];
  return <span className={`dg-st dg-st--${status}`}>{ic || <span className="dg-st__dot" />}</span>;
}

/** Testo completo per l'assistenza: riepilogo + log grezzo. */
function buildReport({ startedAt, portLabel, ev, result, lines, times, t0 }) {
  const info = ev?.info || {};
  const out = [];
  out.push('RaceSense · Diagnostica dispositivo');
  out.push(`Data: ${fmtStamp(startedAt)}`);
  out.push(`Modello: ${info.model || 'non riconosciuto'} · Firmware: ${info.version ? `v${info.version}` : '—'} · ID: ${info.device || '—'}`);
  out.push(`Porta: ${portLabel || '—'} · Browser: ${navigator.userAgent}`);
  if (info.server) out.push(`Server GPS: ${info.server}`);
  if (info.iccid) out.push(`ICCID: ${info.iccid}`);
  if (result) {
    out.push('');
    out.push(`ESITO: ${result.verdict.title}`);
    if (result.warns.length) out.push(`Da verificare: ${result.warns.join(' · ')}`);
    out.push('');
    for (const c of result.list) out.push(`  [${STATUS_LABEL[c.status]}] ${c.label}${c.msg ? ` — ${c.msg}` : ''}${c.detail ? ` (${c.detail})` : ''}`);
    for (const nt of ev.notes || []) out.push(`  Nota: ${nt.msg}`);
  }
  if (ev?.booted) {
    const at = (i) => (i != null && i >= 0 && times[i] != null ? `+${((times[i] - times[ev.bootStart]) / 1000).toFixed(1)} s` : '—');
    out.push('');
    out.push(`Tempi dall'avvio: modem ${at(ev.checks.modem.status === 'ok' ? ev.checks.modem.at : -1)} · SIM ${at(ev.checks.sim.status === 'ok' ? ev.checks.sim.at : -1)} · rete ${at(ev.checks.network.status === 'ok' ? ev.checks.network.at : -1)} · tracking ${at(ev.marks.fullTracking)}`);
  }
  out.push(`Righe ricevute: ${lines.length}${t0 && times.length ? ` in ${((times[times.length - 1] - t0) / 1000).toFixed(1)} s` : ''}`);
  out.push('');
  out.push('──────── LOG ────────');
  out.push(...lines);
  return out.join('\n');
}

export default function DeviceDiagnostics() {
  const support = useMemo(() => serialSupport(), []);
  const [phase, setPhase] = useState('idle');       // idle | opening | running | led | done
  const [error, setError] = useState(null);         // { code, msg }
  const [ev, setEv] = useState(null);
  const [result, setResult] = useState(null);
  const [askReset, setAskReset] = useState(false);
  const [linkLost, setLinkLost] = useState(false);
  const [portLabel, setPortLabel] = useState('');
  const [now, setNow] = useState(Date.now());
  const [lineCount, setLineCount] = useState(0);
  const [copied, setCopied] = useState('');
  const [logOpen, setLogOpen] = useState(false);

  const linkRef = useRef(null);
  const portRef = useRef(null);
  const splitRef = useRef(new LineSplitter());
  const linesRef = useRef([]);
  const timesRef = useRef([]);
  const anyDataRef = useRef(false);
  const t0Ref = useRef(0);
  const startedRef = useRef(new Date());
  const timerRef = useRef(null);
  const endedRef = useRef(true);
  const evRef = useRef(null);
  const lastLenRef = useRef(-1);
  const resetRetryRef = useRef(false);
  const reasonRef = useRef(null);
  const logRef = useRef(null);
  const timing = useRef(NXR_RULES.timing);

  const pushLines = useCallback((list) => {
    if (!list.length) return;
    const t = Date.now();
    const lines = linesRef.current;
    for (const l of list) {
      if (lines.length >= MAX_LINES) break;
      lines.push(l);
      timesRef.current.push(t);
    }
  }, []);

  const onData = useCallback((text) => {
    anyDataRef.current = true;
    pushLines(splitRef.current.push(text));
  }, [pushLines]);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const finish = useCallback(async (reason, opts = {}) => {
    if (endedRef.current) return;
    endedRef.current = true;
    stopTimer();
    reasonRef.current = reason;
    pushLines(splitRef.current.flush());
    const final = evaluate(linesRef.current);
    evRef.current = final;
    setEv(final);
    setLineCount(linesRef.current.length);
    setAskReset(false);
    setLinkLost(false);
    const link = linkRef.current;
    linkRef.current = null;
    if (link) { try { await link.close(); } catch { /* già chiusa */ } }
    if (opts.error) setError(opts.error);
    if (reason === 'done') {
      setPhase('led');
    } else {
      setResult(finalize(final, { reason, anyData: anyDataRef.current }));
      setPhase('done');
    }
  }, [pushLines]);

  const tick = useCallback(() => {
    if (endedRef.current) return;
    const lines = linesRef.current;
    let cur = evRef.current;
    if (lines.length !== lastLenRef.current || !cur) {
      lastLenRef.current = lines.length;
      cur = evaluate(lines);
      evRef.current = cur;
      setEv(cur);
      setLineCount(lines.length);
    }
    const t = Date.now();
    setNow(t);
    const T = timing.current;
    const elapsed = t - t0Ref.current;
    if (cur.done) { finish('done'); return; }
    if (cur.sleeping) { finish('sleep'); return; }
    if (!cur.booted) {
      // bootloader in modalità download: un secondo reset normale, una volta sola
      if (cur.downloadMode && !resetRetryRef.current && linkRef.current) {
        resetRetryRef.current = true;
        linkRef.current.reset(T.resetPulseMs);
      }
      if (elapsed > T.bannerWaitMs) setAskReset(true);
      if (elapsed > T.noDataMs) { finish(anyDataRef.current ? 'timeout' : 'nodata'); return; }
    } else {
      setAskReset(false);
      const bootAt = timesRef.current[cur.bootStart] || t0Ref.current;
      if (t - bootAt > T.totalMs) { finish('timeout'); return; }
    }
    if (elapsed > T.hardLimitMs) finish('timeout');
  }, [finish]);

  const start = useCallback(async (samePort = false) => {
    setError(null);
    setCopied('');
    if (support !== 'ok') return;
    let port = null;
    if (samePort && portRef.current) {
      try {
        const ports = await navigator.serial.getPorts();
        if (ports.includes(portRef.current)) port = portRef.current;
      } catch { /* si richiede di nuovo */ }
    }
    try {
      if (!port) port = await navigator.serial.requestPort();
    } catch (e) {
      const er = serialError(e, 'choose');
      setError(er.code === 'cancel' ? { ...er, soft: true } : er);
      return;
    }
    portRef.current = port;
    setPortLabel(describePort(port).label);
    setPhase('opening');
    setResult(null);
    setEv(null);
    evRef.current = null;
    lastLenRef.current = -1;
    linesRef.current = [];
    timesRef.current = [];
    splitRef.current = new LineSplitter();
    anyDataRef.current = false;
    resetRetryRef.current = false;
    reasonRef.current = null;
    setLineCount(0);
    setAskReset(false);
    setLinkLost(false);
    timing.current = { ...NXR_RULES.timing, ...(window.__NXR_DIAG_TIMING__ || {}) };

    const link = new SerialLink({
      onData,
      options: NXR_RULES.serial,
      reconnectMs: timing.current.reconnectMs,
      onStatus: (s) => {
        if (s.kind === 'lost') setLinkLost(true);
        else if (s.kind === 'back') setLinkLost(false);
        else if (s.kind === 'gone') finish('lost', { error: { code: 'lost', msg: 'Il collegamento con la scheda si è interrotto: il cavo USB si è scollegato? Ricollegalo e ripeti il test.' } });
      },
    });
    try {
      await link.open(port);
    } catch (e) {
      setError(serialError(e, 'open'));
      setPhase('idle');
      return;
    }
    linkRef.current = link;
    endedRef.current = false;
    startedRef.current = new Date();
    t0Ref.current = Date.now();
    setNow(t0Ref.current);
    setPhase('running');
    timerRef.current = setInterval(tick, TICK_MS);
    // riavvio della scheda: si legge l'avvio completo anche se era già accesa
    const okReset = await link.reset(timing.current.resetPulseMs);
    if (!okReset && !endedRef.current) setAskReset(true);
  }, [support, onData, tick, finish]);

  const answerLed = (a) => {
    setResult(finalize(evRef.current, { reason: 'done', led: a, anyData: true }));
    setPhase('done');
  };

  // uscendo dalla pagina la porta si libera sempre
  useEffect(() => () => {
    endedRef.current = true;
    stopTimer();
    const link = linkRef.current;
    linkRef.current = null;
    if (link) link.close();
  }, []);

  // log aperto: segue le righe nuove
  useEffect(() => {
    const el = logRef.current;
    if (logOpen && el && phase === 'running') el.scrollTop = el.scrollHeight;
  }, [lineCount, logOpen, phase]);

  const report = () => buildReport({
    startedAt: startedRef.current, portLabel, ev: evRef.current, result, lines: linesRef.current, times: timesRef.current, t0: t0Ref.current,
  });
  const copyLog = async () => {
    const text = report();
    try {
      await navigator.clipboard.writeText(text);
      setCopied('Log copiato negli appunti: incollalo nella mail o nel messaggio per l\'assistenza.');
    } catch {
      downloadLog(text);
    }
  };
  const downloadLog = (text = report()) => {
    const id = evRef.current?.info?.device || 'scheda';
    const stamp = startedRef.current.toISOString().slice(0, 16).replace(/[:T]/g, '-');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    a.download = `diagnostica-${id}-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    setCopied(`Log salvato nel file ${a.download}.`);
  };

  // ── stato da mostrare ──
  const running = phase === 'running' || phase === 'opening';
  const info = ev?.info || {};
  const shown = useMemo(() => {
    if (result) return result.list;
    const checks = ev?.checks || {};
    let spin = running;
    return CHECKS.map(c => {
      let st = checks[c.id] || { status: 'pending', msg: '', detail: '' };
      if (c.id === 'comm') {
        st = ev?.alive ? { status: 'ok', msg: '', detail: ev.booted ? 'avvio ricevuto' : 'riceve i messaggi della scheda' } : { status: 'pending', msg: '', detail: '' };
      }
      if (c.id === 'led' && phase === 'led') st = { status: 'pending', msg: 'Rispondi alla domanda qui sopra', detail: '' };
      const spinning = spin && st.status === 'pending' && !NO_SPINNER.has(c.id);
      if (spinning) spin = false;
      return { ...c, ...st, spinning };
    });
  }, [ev, result, running, phase]);
  const bootAt = ev?.booted ? timesRef.current[ev.bootStart] : null;
  const elapsed = running ? now - (bootAt || t0Ref.current) : 0;
  const pct = phase === 'running' ? Math.max(ev?.progress?.pct || 0, 2) : phase === 'led' || phase === 'done' ? 100 : 0;
  const otaNote = ev?.notes?.find(n => n.msg.startsWith('Aggiornamento del firmware'));

  if (support !== 'ok') {
    return (
      <div className="rs-page dg-page">
        <PageHeader />
        <div className="dg-unsupported">
          <span className="dg-unsupported__icon"><Icon.Monitor size={26} /></span>
          <div>
            <h2>{support === 'insecure' ? 'Apri la pagina in modo sicuro (HTTPS)' : 'Usa Chrome o Edge su computer'}</h2>
            <p>{support === 'insecure'
              ? 'Il collegamento alla scheda è possibile solo da un indirizzo https://. Apri la piattaforma dal suo indirizzo ufficiale.'
              : 'Questo browser non può collegarsi alla scheda via USB. Apri questa pagina con Google Chrome o Microsoft Edge su un computer Windows, Mac o Linux. Su telefoni e tablet il test non è disponibile.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rs-page dg-page">
      <PageHeader />

      {phase === 'idle' && (
        <section className="dg-intro">
          <div className="dg-intro__main">
            <h2 className="dg-h2">Come funziona</h2>
            <ol className="dg-steps">
              <li><span>1</span><div><strong>Collega la scheda al computer</strong><small>Usa un cavo USB che trasmette dati (alcuni cavi caricano soltanto).</small></div></li>
              <li><span>2</span><div><strong>Premi «Avvia test» e scegli la porta</strong><small>Di solito si chiama «USB Serial», «CP210x», «CH340» o «USB JTAG/serial».</small></div></li>
              <li><span>3</span><div><strong>Attendi fino a 2 minuti</strong><small>La scheda si riavvia e la pagina controlla sensori, GPS, modem, SIM, rete e server.</small></div></li>
            </ol>
            <button type="button" className="rs-btn rs-btn--primary rs-btn--lg dg-start" onClick={() => start(false)}>
              <Icon.Play size={15} /> Avvia test
            </button>
            {error && <div className={`rs-alert ${error.soft ? 'rs-alert--info' : 'rs-alert--danger'}`}>{error.soft ? <Icon.Info /> : <Icon.Alert />}<span>{error.msg}</span></div>}
          </div>
          <aside className="dg-intro__side">
            <h3>Prima di iniziare</h3>
            <ul>
              <li>Chiudi Arduino IDE e gli altri programmi che usano la porta della scheda.</li>
              <li>Tieni la scheda vicino a una finestra o all'aperto: per il test della rete serve il segnale cellulare.</li>
              <li>Il test legge soltanto i messaggi della scheda: non cambia nulla e non invia dati.</li>
              <li>Modelli supportati: NXR01B e NXR01C (riconosciuti da soli).</li>
            </ul>
          </aside>
        </section>
      )}

      {phase !== 'idle' && (
        <>
          <section className="dg-run">
            <div className="dg-id">
              <div className="dg-id__item">
                <small>Modello</small>
                <strong>{info.model || (running ? '—' : 'Non riconosciuto')}</strong>
              </div>
              <div className="dg-id__item"><small>Firmware</small><strong className="dg-mono">{info.version ? `v${info.version}` : '—'}</strong></div>
              <div className="dg-id__item dg-id__item--wide"><small>ID dispositivo</small><strong className="dg-mono">{info.device || '—'}</strong></div>
              <div className="dg-id__item dg-id__item--wide"><small>Porta</small><strong>{portLabel || '—'}</strong></div>
            </div>
            <div className="dg-prog">
              <div className="dg-prog__head">
                <span className="dg-prog__label">
                  {phase === 'opening' ? 'Apertura della porta…'
                    : phase === 'running' ? (ev?.progress?.label || 'Riavvio della scheda…')
                    : phase === 'led' ? 'Avvio completato'
                    : 'Test concluso'}
                </span>
                {running && <span className="dg-prog__time dg-mono">{fmtClock(elapsed)} <small>/ max {fmtClock(timing.current.totalMs)}</small></span>}
              </div>
              <div className={`dg-prog__bar${running ? ' is-running' : ''}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
                <span style={{ width: `${pct}%` }} />
              </div>
            </div>
            {running && (
              <div className="dg-run__actions">
                <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost" onClick={() => finish('stopped')}>Interrompi</button>
              </div>
            )}
          </section>

          {askReset && running && (
            <div className="dg-prompt" role="alert">
              <span className="dg-prompt__icon"><Icon.Zap size={18} /></span>
              <div>
                <strong>Premi il tasto reset della scheda</strong>
                <span>oppure spegnila e riaccendila. La pagina resta in ascolto e riparte da sola quando la scheda si avvia.</span>
              </div>
            </div>
          )}
          {linkLost && running && (
            <div className="rs-alert rs-alert--warning"><Icon.Alert /><span>La porta si è scollegata, riconnessione in corso… Non staccare il cavo.</span></div>
          )}
          {otaNote && running && (
            <div className="rs-alert rs-alert--warning"><Icon.Alert /><span>{otaNote.msg}</span></div>
          )}
          {error && !running && <div className="rs-alert rs-alert--danger"><Icon.Alert /><span>{error.msg}</span></div>}

          {phase === 'led' && (
            <section className="dg-led">
              <span className="dg-led__lamp" aria-hidden="true" />
              <div className="dg-led__text">
                <strong>Il LED della scheda è verde?</strong>
                <span>A fine avvio la scheda accende il LED verde.</span>
              </div>
              <div className="dg-led__btns">
                <button type="button" className="rs-btn rs-btn--primary" onClick={() => answerLed('yes')}><Icon.Check size={15} /> Sì, è verde</button>
                <button type="button" className="rs-btn" onClick={() => answerLed('no')}><Icon.X size={15} /> No</button>
                <button type="button" className="rs-btn rs-btn--ghost" onClick={() => answerLed('unknown')}>Non riesco a vederlo</button>
              </div>
            </section>
          )}

          {phase === 'done' && result && (
            <section className={`dg-verdict dg-verdict--${result.verdict.kind}`}>
              <div className="dg-verdict__head">
                <span className="dg-verdict__icon">
                  {result.verdict.kind === 'ok' ? <Icon.Check size={24} /> : result.verdict.kind === 'fail' ? <Icon.X size={24} /> : <Icon.Alert size={22} />}
                </span>
                <div>
                  <h2>{result.verdict.title}</h2>
                  <p>{result.verdict.text}</p>
                </div>
              </div>
              {(result.warns.length > 0 || ev?.notes?.some(n => n.level === 'info')) && (
                <ul className="dg-verdict__list">
                  {result.warns.map(w => <li key={w} className="is-warn"><Icon.Alert size={13} />{w}</li>)}
                  {(ev?.notes || []).filter(n => n.level === 'info').map(n => <li key={n.msg} className="is-info"><Icon.Info size={13} />{n.msg}</li>)}
                </ul>
              )}
              {reasonRef.current === 'stopped' && <p className="dg-verdict__note">Test interrotto: i controlli non ancora conclusi non sono stati verificati.</p>}
              <div className="dg-verdict__actions">
                <button type="button" className="rs-btn rs-btn--primary" onClick={copyLog}><Icon.Copy size={15} /> Copia log</button>
                <button type="button" className="rs-btn" onClick={() => downloadLog()}><Icon.Download size={15} /> Scarica log</button>
                <button type="button" className="rs-btn rs-btn--ghost" onClick={() => start(true)}><Icon.Refresh size={15} /> Ripeti test</button>
                <button type="button" className="rs-btn rs-btn--ghost" onClick={() => start(false)}>Cambia porta</button>
              </div>
              {copied && <div className="rs-alert rs-alert--success"><Icon.Check /><span>{copied}</span></div>}
            </section>
          )}

          <section className="dg-checks" aria-live="polite">
            {shown.map(c => (
              <div key={c.id} className={`dg-row dg-row--${c.spinning ? 'run' : c.status}`}>
                <StatusIcon status={c.status} spinning={c.spinning} />
                <div className="dg-row__text">
                  <strong>{c.label}</strong>
                  {c.msg ? <span>{c.msg}</span> : c.spinning ? <span>Controllo in corso…</span> : null}
                </div>
                {c.detail ? <span className="dg-row__detail dg-mono">{c.detail}</span> : <span />}
                <span className={`dg-pill dg-pill--${c.spinning ? 'run' : c.status}`}>{c.spinning ? 'In corso' : STATUS_LABEL[c.status]}</span>
              </div>
            ))}
          </section>

          <details className="dg-log" open={logOpen} onToggle={(e) => setLogOpen(e.currentTarget.open)}>
            <summary>
              <Icon.ChevronDown size={15} className="dg-log__chev" />
              Log della scheda <b>{lineCount}</b> righe
              {!running && lineCount > 0 && (
                <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost dg-log__copy" onClick={(e) => { e.preventDefault(); copyLog(); }}>
                  <Icon.Copy size={13} /> Copia log
                </button>
              )}
            </summary>
            <pre ref={logRef} className="dg-log__pre">{logOpen ? linesRef.current.join('\n') || 'Nessun messaggio ricevuto.' : ''}</pre>
          </details>
        </>
      )}
    </div>
  );
}

function PageHeader() {
  return (
    <header className="rs-page-header">
      <div className="rs-page-header__text">
        <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot" />Supporto</span>
        <h1 className="rs-page-header__title">Diagnostica dispositivo</h1>
        <p className="rs-page-header__subtitle">Collega una scheda GPS RaceSense al computer con il cavo USB: in circa due minuti la pagina verifica ogni componente e ti dice se funziona.</p>
      </div>
    </header>
  );
}
